/**
 * Zugriff auf die Tabellen des Dokuments.
 *
 * Vollständig ab WordApi 1.3 verfügbar, also überall dort, wo das Add-in
 * überhaupt startet – hier ist keine Fallunterscheidung nötig.
 */

import { isWordAvailable, WordUnavailableError } from "./document";

export interface TableInfo {
  index: number;
  rowCount: number;
  columnCount: number;
  /** Anzahl der als Überschrift markierten Zeilen – 0, wenn keine gesetzt ist. */
  headerRowCount: number;
  /** Angezeigter Name der Tabellenformatvorlage. */
  styleName: string;
  /** Sprachunabhängiger Name, z. B. "GridTable4_Accent1" oder "TableNormal". */
  styleBuiltIn: string;
  /** Hervorgehobene erste Spalte. */
  firstColumnHighlighted: boolean;
  /** Wechselnde Zeilenfarben ("Verbundene Zeilen"). */
  bandedRows: boolean;
  /** Zellinhalte, Zeile für Zeile. */
  values: string[][];
}

export async function readTables(): Promise<TableInfo[]> {
  if (!isWordAvailable()) throw new WordUnavailableError();

  return Word.run(async (context) => {
    const tables = context.document.body.tables;
    tables.load(
      "items/rowCount,items/headerRowCount,items/style,items/styleBuiltIn," +
        "items/styleFirstColumn,items/styleBandedRows,items/values",
    );
    await context.sync();

    return tables.items.map((table, index) => {
      const values = Array.isArray(table.values) ? table.values : [];
      return {
        index,
        rowCount: typeof table.rowCount === "number" ? table.rowCount : values.length,
        columnCount: values[0]?.length ?? 0,
        headerRowCount: typeof table.headerRowCount === "number" ? table.headerRowCount : 0,
        styleName: typeof table.style === "string" ? table.style.trim() : "",
        styleBuiltIn: typeof table.styleBuiltIn === "string" ? table.styleBuiltIn : "TableNormal",
        firstColumnHighlighted: table.styleFirstColumn === true,
        bandedRows: table.styleBandedRows === true,
        values: values.map((row) => row.map((cell) => (cell ?? "").trim())),
      };
    });
  });
}

/** Hat die Tabelle eine echte, über die Formatvorlage gesetzte Kopfzeile? */
export function hasHeaderRow(table: TableInfo): boolean {
  return table.headerRowCount >= 1;
}

/** Ist der Tabelle eine andere Vorlage als die schlichte Standardtabelle zugewiesen? */
export function hasTableStyle(table: TableInfo): boolean {
  const builtIn = table.styleBuiltIn.toLowerCase();
  return builtIn !== "tablenormal" && builtIn !== "other" && builtIn.length > 0;
}

/** Zellen, die leer geblieben sind – ein häufiger Grund für unklare Tabellen. */
export function countEmptyCells(table: TableInfo): number {
  return table.values.reduce(
    (total, row) => total + row.filter((cell) => cell.length === 0).length,
    0,
  );
}

export interface TableDetails {
  index: number;
  /** Breite jeder Zelle in Punkt, Zeile für Zeile. */
  cellWidths: number[][];
  /** OOXML der Tabelle – verbundene Zellen zeigen sich nur hier. */
  ooxml: string;
}

/**
 * Spaltenbreiten und Markup jeder Tabelle. Braucht nur WordApi 1.3, liest aber
 * jede Zelle einzeln und ist deshalb bei großen Tabellen langsamer als
 * `readTables`.
 */
export async function readTableDetails(): Promise<TableDetails[]> {
  if (!isWordAvailable()) throw new WordUnavailableError();

  return Word.run(async (context) => {
    const tables = context.document.body.tables;
    tables.load("items");
    await context.sync();

    const anfragen = tables.items.map((table) => {
      const rows = table.rows;
      rows.load("items/cells/items/columnWidth");
      const ooxml = table.getRange("Whole").getOoxml();
      return { rows, ooxml };
    });
    await context.sync();

    return anfragen.map(({ rows, ooxml }, index) => ({
      index,
      cellWidths: rows.items.map((row) =>
        row.cells.items.map((cell) => (typeof cell.columnWidth === "number" ? cell.columnWidth : 0)),
      ),
      ooxml: ooxml.value ?? "",
    }));
  });
}
